import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon } from '@vault/ui';
import { useAuth } from '../auth/AuthContext.js';
import { useT } from '../i18n/index.js';
import { CenterShell } from './shared/CenterShell.js';

const ONBOARDED_KEY = 'tv.onboarded';

type Status = 'verifying' | 'error';

/**
 * Consumes ?token from the magic link → exchanges it for a session. First-time
 * users (no `tv.onboarded` flag) land in onboarding; returning users go
 * straight to the timeline. A bad/expired token shows a friendly recovery.
 */
export function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verify } = useAuth();
  const { t } = useT();
  const token = params.get('token');
  const [status, setStatus] = useState<Status>('verifying');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setStatus('error');
      return;
    }
    void (async () => {
      try {
        await verify(token);
        const onboarded = localStorage.getItem(ONBOARDED_KEY) === '1';
        navigate(onboarded ? '/timeline' : '/onboarding', { replace: true });
      } catch {
        setStatus('error');
      }
    })();
  }, [token, verify, navigate]);

  return (
    <CenterShell maxWidth={440}>
      <div style={card}>
        {status === 'verifying' ? (
          <>
            <span style={spinner} />
            <span style={title}>{t('verify.signingIn')}</span>
            <span style={sub}>{t('verify.hangTight')}</span>
          </>
        ) : (
          <>
            <span style={badge}>
              <Icon name="lock" size={24} color="var(--danger)" />
            </span>
            <span style={title}>{t('verify.expired.title')}</span>
            <span style={sub}>
              {t('verify.expired.body')}
            </span>
            <Button variant="secondary" onClick={() => navigate('/login', { replace: true })}>
              {t('verify.backToSignIn')}
            </Button>
          </>
        )}
      </div>
    </CenterShell>
  );
}

const card: CSSProperties = {
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-xl)',
  boxShadow: 'var(--shadow-modal)',
  padding: 32,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
  textAlign: 'center',
};
const title: CSSProperties = {
  fontFamily: 'var(--font-display-cond)',
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '.02em',
  textTransform: 'uppercase',
};
const sub: CSSProperties = { fontSize: 13, lineHeight: 1.6, color: 'var(--text-2)', maxWidth: 320 };
const badge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: 17,
  background: 'var(--danger-tint)',
  border: '1px solid var(--danger-line)',
};
const spinner: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  border: '3px solid var(--surface-3)',
  borderTopColor: 'var(--primary)',
  animation: 'tv-spin .7s linear infinite',
};
